/*
 * <featured-collection-tabs>
 *
 * Tabs of sections/featured-collection.liquid, on the ARIA tabs pattern (arrow keys, Home, End). Each Collection block
 * renders its tab button in a template; the section's inline script moves them into the tab row as the page parses,
 * and placeTabs() does it again after editor re-renders. Only the first tab ships with the page; each hidden tab is a
 * shell that loads once through the Section Rendering API as ?section_id=<section>&page=<100 + its position>, the
 * protocol of assets/component-featured-collections-toggle.js. Loaded only when the section has two or more blocks.
 */
if ( ! customElements.get('featured-collection-tabs') ) {

	class FeaturedCollectionTabs extends HTMLElement {

		connectedCallback(){
			if ( this._ready ) return;
			this._ready = true;

			this.placeTabs();
			this.tabs = [...this.querySelectorAll('[role="tab"]')];
			this.requests = new Map();

			// Collections without products render nothing, so the row can end up with one tab or none
			if ( this.tabs.length < 2 ) {
				this._dropTabs();
				return;
			}

			this.tabs.forEach(tab=>{
				tab.addEventListener('click', ()=>this.select(tab, true));
				// Start loading a hidden tab as soon as the shopper shows intent
				const prefetch = ()=>this.load(this.panelFor(tab)).catch(()=>{});
				tab.addEventListener('pointerenter', prefetch);
				tab.addEventListener('focus', prefetch);
				tab.addEventListener('touchstart', prefetch, { passive: true });
			});
			this.querySelector('[role="tablist"]').addEventListener('keydown', this._onKeydown.bind(this));

			// The editor opens the tab of the selected Collection, or of the Product card or app block inside it
			this.addEventListener('shopify:block:select', event=>{
				const panel = event.target.closest?.('[role="tabpanel"]');
				const tab = panel && this.tabs.find(other=>other.getAttribute('aria-controls') === panel.id);
				if ( tab ) this.select(tab, false);
			});

			// Nothing starts open when the first collection rendered nothing; a page opened with ?page=10N starts on a shell
			const active = this.tabs.find(tab=>tab.getAttribute('aria-selected') === 'true');
			if ( ! active ) this.select(this.tabs[0], false);
			else if ( this.panelFor(active)?.hasAttribute('data-fc-lazy') ) this.select(active, false);
		}

		// Moves each block's tab button from its template into the tab row, once
		placeTabs(){
			const row = this.querySelector('[role="tablist"]');
			if ( ! row ) return;
			this.querySelectorAll('template[data-fc-tab]').forEach(template=>{
				row.append(template.content);
				template.remove();
			});
		}

		// Fewer than two tabs: no tab row, and the panel shows as plain content
		_dropTabs(){
			this.querySelector('[role="tablist"]')?.remove();
			this.querySelectorAll('[role="tabpanel"]').forEach(panel=>{
				panel.removeAttribute('role');
				panel.removeAttribute('aria-labelledby');
				panel.hidden = false;
				if ( panel.hasAttribute('data-fc-lazy') ) {
					this.load(panel)
						.catch(()=>this.load(panel))
						.then(()=>this._layOut(panel), error=>console.warn('[featured-collection]', error));
				}
			});
		}

		panelFor(tab){
			return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
		}

		select(tab, byShopper){
			const panel = this.panelFor(tab);
			if ( ! panel ) return;
			// Read before any panel hides: a loading tab holds this height so the page doesn't jump
			const shown = this.tabs.map(other=>this.panelFor(other)).find(other=>other && ! other.hidden);
			const height = shown?.offsetHeight || 0;

			this.tabs.forEach(other=>{
				const selected = other === tab;
				other.setAttribute('aria-selected', selected);
				other.tabIndex = selected ? 0 : -1;
				const otherPanel = this.panelFor(other);
				if ( otherPanel ) otherPanel.hidden = ! selected;
			});
			this._revealTab(tab, byShopper);

			if ( ! panel.hasAttribute('data-fc-lazy') ) {
				this._layOut(panel);
				return;
			}

			panel.style.minHeight = `${height}px`;
			panel.setAttribute('aria-busy', 'true');
			// One retry (a failed request leaves the cache) before leaving the page
			this.load(panel)
				.catch(()=>this.load(panel))
				.then(
					()=>{
						if ( ! panel.hidden ) this._layOut(panel);
					},
					error=>{
						console.warn('[featured-collection]', error);
						// The shopper chose this tab: show the whole collection rather than an empty panel
						if ( byShopper && ! panel.hidden && panel.dataset.url ) window.location.href = panel.dataset.url;
					}
				)
				.finally(()=>{
					panel.style.removeProperty('min-height');
					panel.removeAttribute('aria-busy');
				});
		}

		// Scrolls the tab row, never the page, so the chosen tab shows whole (smoothly for the shopper's own picks)
		_revealTab(tab, smooth){
			const row = tab.closest('[role="tablist"]');
			if ( ! row ) return;
			const rowBox = row.getBoundingClientRect();
			const tabBox = tab.getBoundingClientRect();
			const shift = tabBox.left < rowBox.left ? tabBox.left - rowBox.left : Math.max(0, tabBox.right - rowBox.right);
			if ( ! shift ) return;
			const motion = smooth && ! window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			row.scrollBy({ left: shift, behavior: motion ? 'smooth' : 'auto' });
		}

		// A slider laid out while its panel was hidden has no widths: measure it again now it shows
		_layOut(panel){
			const slider = panel.querySelector('css-slider');
			if ( ! slider ) return;
			if ( slider.sliderEnabled ) slider.resetSlider();
			else slider.RESIZE_WATCHER?.();
		}

		// Puts a hidden tab's cards in, once
		load(panel){
			if ( ! panel || ! panel.hasAttribute('data-fc-lazy') ) return Promise.resolve();

			return this._fetch(panel).then(html=>{
				// Another call filled it while this one waited
				if ( ! panel.hasAttribute('data-fc-lazy') ) return;
				const fresh = window.OrdoUtils.parseHTML(html).getElementById(panel.id);
				if ( ! fresh || fresh.hasAttribute('data-fc-lazy') ) {
					this.requests.delete(panel);
					throw new Error(`Tab ${panel.dataset.tab} missing from the response`);
				}
				// DOMParser never runs the inline script that moves each card's variant input into its form
				window.OrdoUtils.hydrateQuickBuy(fresh);
				panel.replaceChildren(...fresh.childNodes);
				panel.removeAttribute('data-fc-lazy');
				this.requests.delete(panel);
				window.OrdoUtils.refreshRatings();
			});
		}

		// The tab's section HTML, fetched once and dropped when its cards go in; a failed request leaves the cache so the next try fetches again
		_fetch(panel){
			if ( ! panel || ! panel.hasAttribute('data-fc-lazy') ) return Promise.resolve();

			if ( ! this.requests.has(panel) ) {
				// Full URL, not just the path: ?view= picks the template this section lives in
				const url = new URL(window.location.href);
				url.searchParams.set('section_id', this.dataset.sectionId);
				url.searchParams.set('page', 100 + Number(panel.dataset.tab));

				const request = fetch(url)
					.then(response=>{
						if ( ! response.ok ) throw new Error(`Tab ${panel.dataset.tab} request failed: ${response.status}`);
						return response.text();
					})
					.catch(error=>{
						this.requests.delete(panel);
						throw error;
					});

				this.requests.set(panel, request);
			}

			return this.requests.get(panel);
		}

		_onKeydown(event){
			const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
			if ( index < 0 ) return;
			const rtl = document.documentElement.getAttribute('dir') === 'rtl';
			const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];
			let next;
			if ( step ) {
				next = (index + step + this.tabs.length) % this.tabs.length;
			} else if ( event.key === 'Home' ) {
				next = 0;
			} else if ( event.key === 'End' ) {
				next = this.tabs.length - 1;
			} else {
				return;
			}
			event.preventDefault();
			this.tabs[next].focus();
			this.select(this.tabs[next], true);
		}

	}

	if ( typeof customElements.get('featured-collection-tabs') == 'undefined' ) {
		customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
	}

}
